import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CheckCircle, Clock, Mail, ArrowRight, Sparkles } from 'lucide-react';

const PractitionerOnboardingSuccess: React.FC = () => {
  const navigate = useNavigate();

  const steps = [
    {
      number: 1,
      title: 'Admin Review',
      description: 'The clinic administrator will review your application and credentials',
    },
    {
      number: 2,
      title: 'Approval',
      description: 'Once approved, your account is activated immediately',
    },
    {
      number: 3,
      title: 'Portal Access',
      description: "You'll receive an email with your login credentials and portal access instructions",
    },
  ];

  return (
    <div className="min-h-screen bg-[#F5F0EA] flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Nimbus Logo */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mb-8"
      >
        <img
          src="/img/nim-logo-black.svg"
          alt="Nimbus Healthcare"
          width={140}
          height={40}
          className="w-[140px] h-auto"
        />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 w-full max-w-2xl"
      >
        <div className="rounded-3xl border border-[rgba(26,24,22,0.08)] bg-white shadow-[0_4px_24px_rgba(0,0,0,0.06)] overflow-hidden">
          {/* Header — Brand Teal */}
          <div className="bg-[#4A8A9A] text-white p-8">
            <div className="flex items-center space-x-4">
              <motion.div
                className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              >
                <CheckCircle className="h-8 w-8 text-white" />
              </motion.div>
              <div>
                <h1 className="text-2xl font-bold" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
                  Application Submitted!
                </h1>
                <p className="text-white/80 mt-1 text-sm">Your onboarding request is being reviewed</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-8 space-y-6">
            {/* Success Message */}
            <motion.div
              className="bg-[#4A8A9A]/5 border border-[#4A8A9A]/15 rounded-2xl p-5"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 bg-[#4A8A9A] rounded-xl flex items-center justify-center flex-shrink-0">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#1A1816] mb-1">Thank you for completing your onboarding!</h3>
                  <p className="text-[#57534E] text-sm">
                    Your practitioner application has been successfully submitted to the clinic administrator
                    for review and approval.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* What Happens Next */}
            <motion.div
              className="bg-[#F5F0EA] border border-[rgba(26,24,22,0.08)] rounded-2xl p-5"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-[#22201E] rounded-xl flex items-center justify-center">
                  <Clock className="h-5 w-5 text-[#F5F0EA]" />
                </div>
                <h3 className="font-semibold text-[#1A1816]">What Happens Next?</h3>
              </div>
              <div className="space-y-4 ml-2">
                {steps.map((step, index) => (
                  <motion.div
                    key={step.number}
                    className="flex items-start space-x-3"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                  >
                    <div className="w-7 h-7 bg-[#4A8A9A] rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-semibold text-xs">{step.number}</span>
                    </div>
                    <div>
                      <h4 className="font-medium text-[#1A1816]">{step.title}</h4>
                      <p className="text-[#57534E] text-sm">{step.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Email Notification */}
            <motion.div
              className="bg-[#B87A5D]/5 border border-[#B87A5D]/15 rounded-2xl p-5"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
            >
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 bg-[#B87A5D] rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#1A1816] mb-1">Check Your Email</h3>
                  <p className="text-[#57534E] text-sm">
                    You'll receive an email notification once your account has been approved and activated.
                    Please check your inbox (and spam folder) regularly.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Support Information */}
            <motion.div
              className="border-t border-[rgba(26,24,22,0.08)] pt-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              <h3 className="font-semibold text-[#1A1816] mb-2">Need Help?</h3>
              <p className="text-[#57534E] text-sm mb-4">
                If you have any questions or don't receive a confirmation email within 24-48 hours,
                please contact your clinic administrator or our support team.
              </p>
            </motion.div>

            {/* Action Button — Terracotta CTA */}
            <motion.div
              className="pt-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}>
                <Button
                  onClick={() => navigate('/login')}
                  className="w-full h-12 rounded-full bg-[#B87A5D] hover:bg-[#A56A4D] text-[#F5F0EA] shadow-[0_4px_20px_rgba(184,122,93,0.25)] hover:shadow-[0_8px_32px_rgba(184,122,93,0.35)] font-semibold transition-all duration-300"
                  size="lg"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  Go to Login Page
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default PractitionerOnboardingSuccess;
